import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** Walk up from `el` to the direct child of <body> that contains it (the portal host). */
function closestBodyChild(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el;
  while (node && node.parentElement && node.parentElement !== document.body) {
    node = node.parentElement;
  }
  return node;
}

/**
 * Shared overlay behavior for Modal/Drawer: Esc-to-close, focus trap (Tab cycles within), restore
 * focus to the trigger on close, and body scroll lock. Returns a ref to attach to the panel.
 */
export function useDismissable(open: boolean, onClose: () => void) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;

    // Initial focus into the panel.
    const focusables = panel?.querySelectorAll<HTMLElement>(FOCUSABLE);
    (focusables && focusables.length > 0 ? focusables[0] : panel)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab" && panel) {
        const items = panel.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (items.length === 0) {
          e.preventDefault();
          return;
        }
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // WCAG 4.1.2 / 2.4.3 — make the rest of the app inert while the dialog is open so AT and the
    // Tab order are confined to the modal. We mark every direct child of <body> that is NOT an
    // ancestor of the panel (the portal host) as aria-hidden + inert, and restore them on close.
    const portalHost = panel?.parentElement
      ? closestBodyChild(panel)
      : null;
    const inerted: HTMLElement[] = [];
    for (const child of Array.from(document.body.children)) {
      const el = child as HTMLElement;
      if (el === portalHost) continue;
      if (el.hasAttribute("aria-hidden") || el.hasAttribute("inert")) continue;
      el.setAttribute("aria-hidden", "true");
      el.setAttribute("inert", "");
      inerted.push(el);
    }

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      for (const el of inerted) {
        el.removeAttribute("aria-hidden");
        el.removeAttribute("inert");
      }
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  return panelRef;
}
